import { apiRequest, EmbedAuth, getApiHost, getBasePathname } from 'shared/api';
import { Policyholder, NetworkPolicyholder } from '../domain/policyholder';
import { Environment } from 'models/environment';
import { IdentificationType } from 'policyholder/domain/policyholder-identification';
import { EditPolicyholderFormData } from './upsert-policyholder';
import { JSONObject } from 'shared/utils';

interface UpdateParams {
  organizationId: string;
  auth: EmbedAuth;
  policyholderId: string;
  data: Partial<PolicyholderData>;
  environment: Environment;
  isCompany?: boolean;
}

export const updatePolicyholder = async ({
  organizationId,
  auth,
  policyholderId,
  data,
  environment,
  isCompany,
}: UpdateParams): Promise<Policyholder> => {
  const implicitId =
    data.identification?.type === IdentificationType.Email
      ? data.email
      : data.identification?.type === IdentificationType.Cellphone
      ? data.cellphone?.number
      : undefined;
  const idNumber = data.identification?.number || implicitId;
  const idType = data.identification?.type;

  const body: any = {
    id: isCompany
      ? undefined
      : idType &&
        idNumber && {
          type: idType,
          number: idNumber,
          country: data.identification?.country,
          expiration_date: data.identification?.expirationDate?.toISOString(),
        },
    type: isCompany ? IdentificationType.Company : undefined,
    date_of_birth: data.dateOfBirth?.format('YYYYMMDD'),
    gender: idType !== IdentificationType.Id ? data.gender : undefined,
    first_name: data.firstName,
    middle_name: data.middleName,
    last_name: data.lastName,
    email: data.email === '' ? null : data.email,
    cellphone: data.cellphone ? { number: data.cellphone.number, country: data.cellphone.countryCode } : undefined,
    phone_other: data.phoneOther ? { number: data.phoneOther.number, country: data.phoneOther.countryCode } : undefined,
    address: data.address
      ? {
          google_place_id: data.address.googlePlaceId,
          geo_coordinates_latitude: data.address.geoCoordinatesLatitude,
          geo_coordinates_longitude: data.address.geoCoordinatesLongitude,
          line_1: data.address.line1,
          line_2: data.address.line2,
          suburb: data.address.suburb,
          city: data.address.city,
          area_code: data.address.areaCode,
          country: data.address.country,
        }
      : undefined,
    registration_number: data.registrationNumber,
    company_name: data.companyName,
    company_website_url: data.companyWebsiteUrl,
    contact_position: data.contactPosition,
    subsidiary_companies: data.subsidiaryCompanies,
    date_of_establishment: data.dateOfEstablishment?.format('YYYYMMDD'),
  };

  const response = await apiRequest({
    url: `${getApiHost({ environment })}${getBasePathname({
      organizationId,
      auth,
      clientAppAuth: true,
    })}/policyholders`,
    method: 'patch',
    auth,
    body,
  });

  const network: NetworkPolicyholder = await response.json();
  return Policyholder.fromNetwork(network);
};

export type PolicyholderData = EditPolicyholderFormData & { appData?: JSONObject };
